"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Mail, Lock, LogIn, Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "@/components/layouts";
import { Input, Button, Alert, Spinner, Divider, Text } from "@/components/ui";

export function LoginPageClient() {
    const router = useRouter();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [checkingAuth, setCheckingAuth] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch("/api/auth/me");
                if (response.ok) {
                    router.replace("/dashboard");
                    return;
                }
            } catch {
                // Not logged in, stay on login page
            }
            setCheckingAuth(false);
        };
        checkAuth();
    }, [router]);

    const validateForm = () => {
        if (!email.trim()) {
            setError("이메일을 입력해주세요.");
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError("올바른 이메일 형식이 아닙니다.");
            return false;
        }
        if (!password) {
            setError("비밀번호를 입력해주세요.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const response = await fetch("/api/auth/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, password }),
            });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || "로그인에 실패했습니다.");
            }

            router.push("/dashboard");
        } catch (err) {
            if (err instanceof SyntaxError) {
                setError("서버 응답을 처리할 수 없습니다. 잠시 후 다시 시도해주세요.");
            } else {
                setError(err instanceof Error ? err.message : "로그인 중 오류가 발생했습니다.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (checkingAuth) {
        return (
            <div className="min-h-screen legal-pad-bg flex items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <AuthLayout
            title="다시 오셨군요!"
            subtitle="계정에 로그인하여 노트를 이어서 작성하세요."
            footer={
                <Text variant="small" muted>
                    이 앱은 초대받은 사용자만 이용할 수 있습니다.
                </Text>
            }
        >
            {error && (
                <Alert
                    type="error"
                    message={error}
                    onClose={() => setError(null)}
                    className="mb-6 animate-reveal"
                />
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                    label="이메일"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoComplete="email"
                    variant="legalPad"
                    leftIcon={<Mail className="w-4 h-4" />}
                />

                <Input
                    label="비밀번호"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호를 입력하세요"
                    autoComplete="current-password"
                    variant="legalPad"
                    leftIcon={<Lock className="w-4 h-4" />}
                    rightIcon={
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                            aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                        >
                            {showPassword ? (
                                <EyeOff className="w-4 h-4" />
                            ) : (
                                <Eye className="w-4 h-4" />
                            )}
                        </button>
                    }
                />

                <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    isLoading={isLoading}
                    leftIcon={<LogIn className="w-4 h-4" />}
                >
                    {isLoading ? "로그인 중..." : "로그인"}
                </Button>
            </form>

            <Divider label="또는" className="my-8" />

            <p className="text-center text-sm text-[var(--color-text-secondary)]">
                아직 계정이 없으신가요?{" "}
                <Link
                    href="/signup"
                    className="font-medium text-[var(--color-link)] hover:text-[var(--color-link-hover)] underline underline-offset-2 transition-colors"
                >
                    회원가입
                </Link>
            </p>
        </AuthLayout>
    );
}
